import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaCalendarCheck,
  FaCalendarAlt,
  FaFileAlt,
  FaUsers,
  FaChair,
  FaCheckCircle,
  FaEnvelope,
  FaPlus,
  FaSyncAlt,
  FaExclamationTriangle
} from "react-icons/fa";
import { fetchWithAuth } from "../../../utils/api";
import CreateEventModal from "./CreateEventModal";
import "./EventsOverview.css";

const EventsOverview = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalEvents: 0,
    publishedEvents: 0,
    draftEvents: 0,
    totalRegistrations: 0,
    totalSeats: 0,
    bookedSeats: 0,
    remainingSeats: 0,
    fullyBookedEvents: 0,
    emailSending: {
      Sent: 0,
      Sending: 0,
      Failed: 0,
      Retrying: 0
    }
  });

  const [recentEvents, setRecentEvents] = useState([]);
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchWithAuth("/admin/events/dashboard-stats");
      const data = await res.json();

      if (res.ok && data.success) {
        setStats(data.stats);
        setRecentEvents(data.recentEvents || []);
        setRecentRegistrations(data.recentRegistrations || []);
      } else {
        setError(data.message || "Failed to load dashboard metrics");
      }
    } catch (err) {
      console.error("Dashboard metrics error:", err);
      setError("Network error connecting to backend service");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const kpiCards = [
    {
      title: "Total Events",
      value: stats.totalEvents,
      hint: "All draft & published events",
      icon: <FaCalendarAlt />,
      colorClass: "cyan",
      link: "/admin/events/all"
    },
    {
      title: "Published Events",
      value: stats.publishedEvents,
      hint: "Live on public website",
      icon: <FaCalendarCheck />,
      colorClass: "emerald",
      link: "/admin/events/all?status=Published"
    },
    {
      title: "Draft Events",
      value: stats.draftEvents,
      hint: "Unpublished internal drafts",
      icon: <FaFileAlt />,
      colorClass: "amber",
      link: "/admin/events/all?status=Draft"
    },
    {
      title: "Total Registrations",
      value: stats.totalRegistrations,
      hint: "Confirmed attendees in MongoDB",
      icon: <FaUsers />,
      colorClass: "purple",
      link: "/admin/events/registrations"
    },
    {
      title: "Total Seats",
      value: stats.totalSeats,
      hint: "Combined event capacity",
      icon: <FaChair />,
      colorClass: "cyan",
      link: "/admin/events/all"
    },
    {
      title: "Remaining Seats",
      value: stats.remainingSeats,
      hint: "Available across all sessions",
      icon: <FaUsers />,
      colorClass: "emerald",
      link: "/admin/events/all"
    },
    {
      title: "Fully Booked Events",
      value: stats.fullyBookedEvents,
      hint: "Sessions with 100% seats booked",
      icon: <FaCheckCircle />,
      colorClass: "rose",
      link: "/admin/events/all"
    },
    {
      title: "Emails Dispatched",
      value: stats.emailSending.Sent,
      hint: `${stats.emailSending.Failed} failed / ${stats.emailSending.Retrying} retrying`,
      icon: <FaEnvelope />,
      colorClass: "cyan",
      link: "/admin/events/registrations"
    }
  ];

  return (
    <div className="events-overview-container">
      {/* HEADER BANNER CARD */}
      <div className="events-header-card">
        <div className="events-header-text">
          <span className="events-header-badge">🎟️ Events Management</span>
          <h1>Events Overview & Live Analytics</h1>
          <p>
            Real-time MongoDB statistics for event schedules, seat reservation capacity, attendee registrations, and Brevo email confirmations.
          </p>
        </div>

        <div className="events-header-actions">
          <button
            type="button"
            className="events-btn-secondary"
            onClick={loadDashboardData}
            title="Refresh dashboard metrics"
          >
            <FaSyncAlt className={loading ? "spin-icon" : ""} />
            <span>Refresh</span>
          </button>

          <button
            type="button"
            className="events-btn-primary"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <FaPlus />
            <span>Create Event</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="events-overview-error">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}

      {/* KPI METRIC CARDS GRID */}
      <div className="events-kpi-grid">
        {kpiCards.map((card, idx) => (
          <div
            key={idx}
            className={`events-kpi-card ${card.colorClass}`}
            onClick={() => navigate(card.link)}
          >
            <div className="kpi-card-header">
              <span className="kpi-card-title">{card.title}</span>
              <span className="kpi-card-icon">{card.icon}</span>
            </div>
            <div className="kpi-card-value">{loading ? "..." : card.value}</div>
            <div className="kpi-card-hint">{card.hint}</div>
          </div>
        ))}
      </div>

      {/* TWO COLUMN RECENT TABLES */}
      <div className="events-dashboard-tables-grid">
        {/* RECENT EVENTS */}
        <div className="events-table-panel">
          <div className="panel-header">
            <h3>Recent Events</h3>
            <Link to="/admin/events/all" className="panel-view-all">
              View All Events →
            </Link>
          </div>

          <div className="panel-table-wrapper">
            {recentEvents.length === 0 ? (
              <div className="panel-empty-state">No events created yet. Click "Create Event" to begin.</div>
            ) : (
              <table className="panel-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Date (IST)</th>
                    <th>Seats</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentEvents.map((ev) => (
                    <tr key={ev._id}>
                      <td>
                        <div className="event-table-cell">
                          <img src={ev.bannerImage} alt={ev.title} className="table-thumb" />
                          <div>
                            <span className="table-title">{ev.title}</span>
                            <span className="table-sub">{ev.slug}</span>
                          </div>
                        </div>
                      </td>
                      <td>{ev.eventDate}</td>
                      <td>
                        <span className="table-seats-badge">
                          {ev.bookedSeats || 0} / {ev.totalSeats}
                        </span>
                      </td>
                      <td>
                        <span className={`status-pill ${ev.status?.toLowerCase()}`}>
                          {ev.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* RECENT REGISTRATIONS */}
        <div className="events-table-panel">
          <div className="panel-header">
            <h3>Recent Attendee Registrations</h3>
            <Link to="/admin/events/registrations" className="panel-view-all">
              Manage Registrations →
            </Link>
          </div>

          <div className="panel-table-wrapper">
            {recentRegistrations.length === 0 ? (
              <div className="panel-empty-state">No registrations recorded yet.</div>
            ) : (
              <table className="panel-table">
                <thead>
                  <tr>
                    <th>Attendee</th>
                    <th>Event</th>
                    <th>Registration ID</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRegistrations.map((reg) => (
                    <tr key={reg._id}>
                      <td>
                        <div>
                          <b className="table-title">{reg.customerName}</b>
                          <span className="table-sub">{reg.customerEmail}</span>
                        </div>
                      </td>
                      <td>
                        <span className="table-title">{reg.eventTitleSnapshot || "Session"}</span>
                      </td>
                      <td>
                        <span className="table-reg-id">{reg.registrationId}</span>
                      </td>
                      <td>
                        <span className={`email-status-tag ${reg.emailStatus?.toLowerCase()}`}>
                          {reg.emailStatus || "Pending"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* CREATE EVENT MODAL */}
      <CreateEventModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onEventCreated={(newEvent) => {
          loadDashboardData();
        }}
      />
    </div>
  );
};

export default EventsOverview;
